import { ReactHookFormDevtools } from "#src/components/_development/ReactHookFormDevtools";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { Icon, Tab, Tabs } from "@validereinc/common-components";
import classNames from "classnames/bind";
import { nanoid } from "nanoid";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { create } from "zustand";
import styles from "./DevTools.module.css";

export type useDevtoolsType = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedTab: "react-query" | "react-hook-form";
  setSelectedTab: (selectedTab: "react-query" | "react-hook-form") => void;
};

export const useDevtools = create<useDevtoolsType>((set) => ({
  isOpen: false,
  setIsOpen: (isOpen) => {
    set({ isOpen });
  },
  selectedTab: "react-hook-form",
  setSelectedTab: (selectedTab) => {
    set({ selectedTab });
  },
}));

const cx = classNames.bind(styles);

export const DevTools = () => {
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(0);
  const contentPopoverId = useRef(nanoid(8));
  const { selectedTab, setSelectedTab } = useDevtools();
  const [isOpen, setIsOpen] = useState(false);
  const [height, setHeight] = useState(() => `${window.innerHeight * 0.25}px`);
  const [isTriggerHoveredOrFocused, setIsTriggerHoveredOrFocused] =
    useState(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!(e.target as HTMLElement).closest(`.${cx("drag-handle")}`)) return;

      isDragging.current = true;
      startY.current = e.clientY;
      startHeight.current = parseInt(height);
      document.body.style.cursor = "row-resize";
    },
    [height]
  );

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    document.body.style.cursor = "default";
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current) return;

    e.preventDefault();

    const delta = startY.current - e.clientY;

    if (delta === 0) return; // when there's no difference, do nothing

    const newHeight = Math.min(
      Math.max(startHeight.current + delta, 200), // minimum 200px
      window.innerHeight - 100 // maximum window height - 100px
    );

    setHeight(`${newHeight}px`);
  }, []);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <>
      <button
        type="button"
        className={cx("trigger", isOpen && "trigger--active")}
        aria-label={
          isOpen ? "Close Development Tools" : "Open Development Tools"
        }
        title={isOpen ? "Close Development Tools" : "Open Development Tools"}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={contentPopoverId.current}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsTriggerHoveredOrFocused(true)}
        onMouseLeave={() => setIsTriggerHoveredOrFocused(false)}
        onFocus={() => setIsTriggerHoveredOrFocused(true)}
        onBlur={() => setIsTriggerHoveredOrFocused(false)}
      >
        {isOpen ? (
          <>
            <Icon
              variant="x"
              size={16}
              weight="bold"
              style={{ flexShrink: 0 }}
            />
            <span
              className={cx(
                "trigger-label",
                isTriggerHoveredOrFocused && "trigger-label--visible"
              )}
            >
              Close
            </span>
          </>
        ) : (
          <>
            <Icon
              variant="toolbox"
              size={16}
              weight="bold"
              style={{ flexShrink: 0 }}
            />
            <span
              className={cx(
                "trigger-label",
                isTriggerHoveredOrFocused && "trigger-label--visible"
              )}
            >
              Open Dev Tools
            </span>
          </>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className={cx("drag-handle")}
            onMouseDown={handleMouseDown}
            style={{
              bottom: height,
            }}
          >
            <div
              className={cx("drag-handle-indicator")}
              aria-label="Drag me to resize"
            />
          </div>
          <div
            className={cx("content")}
            style={{
              height,
            }}
            id={contentPopoverId.current}
          >
            <Tabs
              activeKey={selectedTab}
              onChange={(newTab) =>
                setSelectedTab(newTab as useDevtoolsType["selectedTab"])
              }
              navBarProps={{
                className: cx("tabs-navbar"),
              }}
            >
              <Tab
                title="TanStack Query"
                tabKey="react-query"
              >
                <ReactQueryDevtoolsPanel
                  isOpen
                  setIsOpen={() => setIsOpen(false)}
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  onDragStart={() => {}}
                  style={{
                    height,
                  }}
                />
              </Tab>
              <Tab
                title="React Hook Form"
                tabKey="react-hook-form"
              >
                <ReactHookFormDevtools
                  style={{
                    height,
                  }}
                />
              </Tab>
            </Tabs>
          </div>
        </>
      )}
    </>
  );
};
